import React from 'react';
import { motion } from 'framer-motion';
import {
  FaReact, FaNodeJs, FaVuejs, FaAngular, FaDocker, FaGitAlt, FaHtml5, FaCss3Alt,
  FaGraduationCap, FaPaintBrush, FaPenNib,
} from 'react-icons/fa';
import {
  SiJavascript, SiTypescript, SiMongodb, SiExpress, SiFirebase,
  SiRedux, SiTailwindcss,
} from 'react-icons/si';
import { usePageData } from '../hooks/useFirestoreData';
import '../styles/Skills.css';

/*
  Firestore doc: pages/skills
  {
    categories: [
      {
        title: "Frontend",
        color: "var(--neon-blue)",
        skills: [
          { name: "JavaScript", icon: "javascript", level: 95 },
          ...
        ]
      },
      ...
    ],
    education: [
      {
        degree: "Bachelor's in Computer Science",
        institution: "University of Technology",
        year: "2014 — 2018",
        highlights: "Data Structures, Algorithms, ..."
      },
      ...
    ]
  }
*/

/* Map icon string keys to React icon components */
const SKILL_ICON_MAP = {
  javascript: <SiJavascript />,
  react: <FaReact />,
  vue: <FaVuejs />,
  angular: <FaAngular />,
  typescript: <SiTypescript />,
  html5: <FaHtml5 />,
  css3: <FaCss3Alt />,
  redux: <SiRedux />,
  tailwind: <SiTailwindcss />,
  node: <FaNodeJs />,
  express: <SiExpress />,
  mongodb: <SiMongodb />,
  firebase: <SiFirebase />,
  git: <FaGitAlt />,
  docker: <FaDocker />,
  photoshop: <FaPaintBrush />,
  xd: <FaPenNib />,
};

const DEFAULT_SKILLS = {
  categories: [
    {
      title: 'Frontend',
      color: 'var(--neon-blue)',
      skills: [
        { name: 'JavaScript', icon: 'javascript', level: 95 },
        { name: 'React', icon: 'react', level: 92 },
        { name: 'Vue.js', icon: 'vue', level: 80 },
        { name: 'Angular', icon: 'angular', level: 75 },
        { name: 'TypeScript', icon: 'typescript', level: 85 },
        { name: 'HTML5', icon: 'html5', level: 98 },
        { name: 'CSS3', icon: 'css3', level: 95 },
        { name: 'Redux', icon: 'redux', level: 82 },
        { name: 'Tailwind', icon: 'tailwind', level: 88 },
      ],
    },
    {
      title: 'Backend',
      color: 'var(--neon-purple)',
      skills: [
        { name: 'Node.js', icon: 'node', level: 90 },
        { name: 'Express', icon: 'express', level: 88 },
        { name: 'MongoDB', icon: 'mongodb', level: 85 },
        { name: 'Firebase', icon: 'firebase', level: 78 },
      ],
    },
    {
      title: 'Tools & DevOps',
      color: 'var(--neon-cyan)',
      skills: [
        { name: 'Git', icon: 'git', level: 92 },
        { name: 'Docker', icon: 'docker', level: 72 },
      ],
    },
    {
      title: 'Design',
      color: 'var(--neon-pink)',
      skills: [
        { name: 'Photoshop', icon: 'photoshop', level: 80 },
        { name: 'Adobe XD', icon: 'xd', level: 75 },
      ],
    },
  ],
  education: [
    {
      degree: 'Masters of Computer Application',
      institution: 'Sikkim Manipal University, Sikkim',
      year: '2012 — 2016',
      highlights: 'Computer Science',
    },
    {
      degree: 'Bachelors of Business Administration',
      institution: 'Annamalai University, Tamil Nadu',
      year: '2008 — 2011',
      highlights: 'Management & Finance',
    },
    {
      degree: 'Intermediate of Science',
      institution: 'BSEB, Patna',
      year: '2005 — 2007',
      highlights: 'PCM',
    },
    {
      degree: 'High School (10th)',
      institution: 'BSEB, Patna',
      year: '2004 — 2005',
      highlights: '',
    },
  ],
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.25, 0.8, 0.25, 1] },
  }),
};

function SkillBar({ name, icon, level, color, delay }) {
  const iconComponent = typeof icon === 'string' ? (SKILL_ICON_MAP[icon] || null) : icon;

  return (
    <motion.div
      className="skill-bar-item"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeInUp}
      custom={delay}
    >
      <div className="skill-bar-header">
        <span className="skill-bar-icon" style={{ color }}>{iconComponent}</span>
        <span className="skill-bar-name">{name}</span>
        <span className="skill-bar-level">{level}%</span>
      </div>
      <div className="skill-bar-track">
        <motion.div
          className="skill-bar-fill"
          style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }}
          initial={{ width: 0 }}
          whileInView={{ width: `${level}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: delay * 0.1 + 0.3, ease: [0.25, 0.8, 0.25, 1] }}
        />
      </div>
    </motion.div>
  );
}

function Skills() {
  const { data } = usePageData('skills', DEFAULT_SKILLS);

  return (
    <section className="skills page-wrapper">
      <div className="skills-container">
        <motion.h1
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Skills & Education
        </motion.h1>

        <div className="skills-grid">
          {data.categories.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              className="glass-card skill-category-card"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              custom={catIndex}
            >
              <h2 className="category-title" style={{ color: category.color }}>
                {category.title}
              </h2>
              <div className="category-skills">
                {category.skills.map((skill, i) => (
                  <SkillBar
                    key={i}
                    {...skill}
                    color={category.color}
                    delay={i}
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="education-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          custom={1}
        >
          <h2 className="education-heading">
            <FaGraduationCap /> Education
          </h2>
          <div className="education-grid">
            {data.education.map((edu, i) => (
              <motion.div
                key={i}
                className="glass-card education-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                custom={i + 1}
                whileHover={{ scale: 1.02 }}
              >
                <span className="edu-year">{edu.year}</span>
                <h3 className="edu-degree">{edu.degree}</h3>
                <h4 className="edu-institution">{edu.institution}</h4>
                <p className="edu-highlights">{edu.highlights}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Skills;
